// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { AvailablePlayer, Player } from './types';

const MAX_PLAYERS = 8;

export const normalizeDebugParam = (debugParam?: string | string[]) => {
  const values = (Array.isArray(debugParam) ? debugParam : [debugParam])
    .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .filter((value) => value.length > 0);

  if (values.includes('false')) {
    return false;
  }

  return values.includes('true');
};

export const deriveInitialTourId = (tourParam: string | string[] | undefined, storedTourId: string | null) => {
  const raw = Array.isArray(tourParam) ? tourParam[0] : tourParam;
  const candidate = raw?.trim() || storedTourId;
  if (!candidate) return null;
  if (candidate === '00000000-0000-0000-0000-000000000000') return null;
  if (candidate === 'single-round') return null;
  return candidate;
};

export const buildPlayerOrder = (
  incomingPlayers: Player[],
  currentUserPlayer: AvailablePlayer | undefined,
  createId: () => string
) => {
  const orderedPlayers: Player[] = [];
  if (currentUserPlayer) {
    orderedPlayers.push({
      id: currentUserPlayer.id,
      name: currentUserPlayer.name,
      scp: incomingPlayers.find((player) => player.id === currentUserPlayer.id)?.scp ?? '',
    });
  }
  incomingPlayers.forEach((player) => {
    if (currentUserPlayer && player.id === currentUserPlayer.id) return;
    orderedPlayers.push(player);
  });
  return orderedPlayers.length ? orderedPlayers.slice(0, MAX_PLAYERS) : [{ id: createId(), name: '', scp: '' }];
};

export const buildEmptyScpIds = (players: Player[], availablePlayerIdSet: Set<string>) =>
  players
    .filter((player) => !player.scp.trim() && availablePlayerIdSet.has(player.id))
    .map((player) => player.id)
    .sort();

export const upsertAvailablePlayer = (
  availablePlayers: AvailablePlayer[],
  incomingPlayer: AvailablePlayer
) => {
  const incomingId = incomingPlayer.id.trim();
  if (!incomingId) {
    return availablePlayers;
  }

  const resolvedName = incomingPlayer.name.trim() || incomingId;
  const nextPlayer: AvailablePlayer = {
    id: incomingId,
    name: resolvedName,
    userId: incomingPlayer.userId ?? null,
    isTemporary: incomingPlayer.isTemporary ?? incomingPlayer.userId == null,
    isFriend: incomingPlayer.isFriend ?? false,
  };

  let updated = false;
  const merged = availablePlayers.map((player) => {
    if (player.id !== incomingId) {
      return player;
    }
    updated = true;
    return {
      ...player,
      ...nextPlayer,
    };
  });

  const withPlayer = updated ? merged : [...merged, nextPlayer];
  return [...withPlayer].sort((left, right) => {
    const leftFriend = left.isFriend === true;
    const rightFriend = right.isFriend === true;
    if (leftFriend !== rightFriend) {
      return leftFriend ? -1 : 1;
    }
    const byName = left.name.localeCompare(right.name);
    if (byName !== 0) {
      return byName;
    }
    return left.id.localeCompare(right.id);
  });
};

export const appendPlayerRowIfMissing = (
  players: Player[],
  incomingPlayer: Player,
  maxPlayers: number = MAX_PLAYERS
) => {
  const incomingId = incomingPlayer.id.trim();
  if (!incomingId) {
    return players;
  }

  const existing = players.find((player) => player.id === incomingId);
  if (existing) {
    return players.map((player) =>
      player.id === incomingId
        ? {
            ...player,
            name: incomingPlayer.name.trim() || player.name,
          }
        : player
    );
  }

  if (players.length >= maxPlayers) {
    return players;
  }

  return [
    ...players,
    {
      id: incomingId,
      name: incomingPlayer.name.trim() || incomingId,
      scp: incomingPlayer.scp ?? '',
    },
  ];
};
